<template>
  <div id="searchResultEl" class="p-search-modal" :class="activeClass">
    <!-- search modal header -->
    <div class="p-search-modal__header-container">
      <div class="container">
        <div class="p-search-modal__header">
          <div class="p-search-modal__header__title">
            {{ dictionaryData.search }}
          </div>

          <div v-if="configuration.isWeb" class="p-search-modal__header__icon">
            <CloseModal @close="closeModal" />
          </div>
        </div>
      </div>
    </div>
    <div class="container">
      <div class="p-search-modal__wrapper">
        <!-- sticky header -->
        <transition name="fade" mode="out-in">
          <StickyHeader v-if="showStickyHeader" :title="dictionaryData.search">
            <template #sticky-header-content>
              <SearchInput
                v-model="searchInput"
                :placeholder="placeholderData"
                :total-result="totalSearchResult"
                :search-in-progress="searchInProgress" />
              <SearchResultTags
                v-model="currentActiveResult"
                :total-articles="totalResult.totalArticles"
                :total-movies="totalResult.totalMovies"
                :titles="{
                  movies: dictionaryData.movies,
                  articles: dictionaryData.articles,
                }" />
              <div v-if="configuration.isWeb" class="p-search-modal__header__icon">
                <CloseModal @close="closeModal" />
              </div>
            </template>
          </StickyHeader>
        </transition>
        <!-- search input -->
        <SearchInput
          v-model="searchInput"
          show-total-counter
          :placeholder="placeholderData"
          :total-result="totalSearchResult"
          :search-in-progress="searchInProgress" />

        <div v-if="!noResultFound" class="p-search-modal__tab-area">
          <!-- search keywords -->
          <SearchKeywords
            v-if="!searchInput"
            :title="dictionaryData.keywords"
            :keywords="initialKeywords"
            show-title
            @choose-keyword="searchKeyword" />
          <!-- search result tags -->
          <SearchResultTags
            v-model="currentActiveResult"
            :total-articles="totalResult.totalArticles"
            :total-movies="totalResult.totalMovies"
            :titles="{
              movies: dictionaryData.movies,
              articles: dictionaryData.articles,
            }" />
          <!-- search results -->
          <SearchResult
            :current-active-result="currentActiveResult"
            :movie-result="searchResults.movieResult"
            :article-result="searchResults.articleResult"
            :dictionary="dictionaryData"
            :configuration="configuration" />
        </div>
        <!-- in case there are no search results found -->
        <NoSearchResult
          v-if="noResultFound"
          :no-result-sentence="dictionaryData.noSearchResults">
          <SearchKeywords
            :title="dictionaryData.keywords"
            :keywords="initialKeywords"
            @choose-keyword="searchKeyword" />
        </NoSearchResult>
      </div>
    </div>
  </div>
</template>

<script setup>
import { getData } from '../../lib/lib'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import SearchInput from '../components/search/SearchInput.vue'
import SearchKeywords from '../components/search/SearchKeywords.vue'
import SearchResultTags from '../components/search/SearchResultTags.vue'
import SearchResult from '../components/search/SearchResult.vue'
import StickyHeader from '../components/search/StickyHeader.vue'
import NoSearchResult from '../components/search/NoSearchResult.vue'
import CloseModal from '../components/UI/CloseModal.vue'
import * as lib from './../../lib/lib'

const { dictionary, configuration, keywords } = getData('searchModalData')
import axios from 'axios'
import debounce from 'lodash.debounce'
let scrollListener = null

const active = ref(false)
const initialKeywords = ref([])
const dictionaryData = ref(dictionary)
const placeholderData = ref(dictionary.placeholder)
const searchInput = ref('')
const currentActiveResult = ref('movie')
const searchResults = ref({
  movieResult: [],
  articleResult: [],
})
const showStickyHeader = ref(false)

const totalResult = ref({
  totalMovies: 0,
  totalArticles: 0,
})

const searchInProgress = ref(false)

const noResultFound = computed(() => {
  return (
    !searchInProgress.value &&
    totalSearchResult.value === 0 &&
    searchInput.value
  )
})

const activeClass = computed(() => {
  return active.value ? 'active' : ''
})

const totalSearchResult = computed(() => {
  return totalResult.value.totalArticles + totalResult.value.totalMovies
})

const activateModal = () => {
  active.value = true
  jQuery('html, body').addClass('search-modal-opened')
}
const closeModal = () => {
  if (!configuration.isWeb) return;
  active.value = false
  searchInput.value = ''
  jQuery('html, body').removeClass('search-modal-opened')
}

onMounted(async () => {
  jQuery(document).on('keyup.modal', (e) => {
    if (e.key === 'Escape') {
      // escape key maps to keycode `27`
      closeModal()
    }
  })

  jQuery(document).on('search', () => {
    activateModal()
  })
  getInitialKeywords()
  startScrollListener()
  if (!configuration.isWeb){
    jQuery(document).trigger("search")
  }
})

const getInitialKeywords = () => {
  initialKeywords.value = keywords
  dictionaryData.value = dictionary
}

const searchKeyword = (keyword) => {
  searchInput.value = keyword
}

watch(searchInput, () => {
  if (searchInput.value.length >= 3) {
    searchInProgress.value = true
    handleSearch()
  } else {
    resetResults()
  }
})

const handleSearch = debounce(async () => {
  try {
    const moviesResponse = await axios.post(
      configuration.handlerSearchMovies,
      {
        search: searchInput.value,
      },
      lib.axiosConfig
    )
    const articlesResponse = await axios.post(
      configuration.handlerSearchArticles,
      {
        search: searchInput.value,
      },
      lib.axiosConfig
    )
    handleResponse(moviesResponse, articlesResponse)
  } catch (error) {
    console.log(error)
  }
  searchInProgress.value = false
}, 1000)
const emit = defineEmits(['update:modelValue'])

const handleResponse = (moviesResponse, articlesResponse) => {
  if (moviesResponse.data.data.length || articlesResponse.data.data.length) {
    const moviesData = moviesResponse.data.data
    const articlesData = articlesResponse.data.data
    searchResults.value = {
      ...searchResults.value,
      articleResult: articlesData,
    }
    searchResults.value = { ...searchResults.value, movieResult: moviesData }

    totalResult.value = { ...totalResult.value, totalMovies: moviesData.length }
    totalResult.value = {
      ...totalResult.value,
      totalArticles: articlesData.length,
    }
    if (!moviesResponse.data.data.length) {
      currentActiveResult.value = 'article'
    }
  } else {
    // show empty result
  }
}

const resetResults = () => {
  searchResults.value = {
    articleResult: [],
    movieResult: [],
  }
  totalResult.value = { totalMovies: 0, totalArticles: 0 }
  showStickyHeader.value = false
}

const startScrollListener = () => {
  const searchModel = document.getElementById('searchResultEl')
  let oldScrollY = 0
  scrollListener = searchModel.addEventListener('scroll', () => {
    const modelScrollHeight = Math.round(searchModel.scrollTop)
    // this means user scrolling down
    if (oldScrollY < modelScrollHeight && modelScrollHeight >= 540) {
      showStickyHeader.value = true
      // user start to scroll up
    } else if (oldScrollY > modelScrollHeight && oldScrollY < 930) {
      showStickyHeader.value = false
    }
    oldScrollY = modelScrollHeight
  })
}
onBeforeUnmount(() => {
  scrollListener.removeEventListener(startScrollListener())
})
</script>
